import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Search, ShieldCheck, Infinity as InfinityIcon, Headphones, Zap, Star, ArrowRight, MapPin, Calendar } from "lucide-react";
import { Button } from "../components/ui/button";
import { useT } from "../i18n";
import { api } from "../lib/api";
import { useSettings } from "../lib/useSettings";

const HERO = "https://images.unsplash.com/photo-1551952237-954a0e68786c?w=1920";
const WHY_ICONS = [ShieldCheck, InfinityIcon, Headphones, Zap];

function SearchWidget() {
  const { t } = useT();
  const navigate = useNavigate();
  const [form, setForm] = useState({ location: "", pickup_date: "", return_date: "" });

  const submit = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (form.location) params.set("loc", form.location);
    if (form.pickup_date) params.set("pickup", form.pickup_date);
    if (form.return_date) params.set("return", form.return_date);
    navigate(`/fleet?${params.toString()}`);
  };

  const field = "flex flex-col gap-1.5";
  const label = "text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1";
  const input = "border border-input bg-background px-3 py-2.5 text-sm rounded-md focus:outline-none focus:ring-2 focus:ring-accent";

  return (
    <form onSubmit={submit} data-testid="hero-search-widget"
      className="bg-white border border-border rounded-lg shadow-2xl p-5 grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
      <div className={field}>
        <label className={label}><MapPin className="h-3 w-3" /> {t("hero.pickup_location")}</label>
        <input className={input} data-testid="search-location" placeholder={t("hero.location_placeholder")}
          value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
      </div>
      <div className={field}>
        <label className={label}><Calendar className="h-3 w-3" /> {t("hero.pickup_date")}</label>
        <input type="datetime-local" className={input} data-testid="search-pickup-date"
          value={form.pickup_date} onChange={(e) => setForm({ ...form, pickup_date: e.target.value })} />
      </div>
      <div className={field}>
        <label className={label}><Calendar className="h-3 w-3" /> {t("hero.return_date")}</label>
        <input type="datetime-local" className={input} data-testid="search-return-date"
          value={form.return_date} onChange={(e) => setForm({ ...form, return_date: e.target.value })} />
      </div>
      <Button type="submit" size="lg" className="rounded-md bg-accent hover:bg-accent/90 h-11 gap-2" data-testid="hero-search-button">
        <Search className="h-4 w-4" /> {t("hero.search")}
      </Button>
    </form>
  );
}

export default function Home() {
  const { t, lang } = useT();
  const navigate = useNavigate();
  const s = useSettings();
  const { data: categories = [] } = useQuery({ queryKey: ["categories"], queryFn: async () => (await api.get("/categories")).data });
  const { data: testimonials = [] } = useQuery({ queryKey: ["testimonials"], queryFn: async () => (await api.get("/testimonials")).data });

  const whyItems = s.why_us || [];

  return (
    <div>
      {/* Hero */}
      <section className="relative min-h-[640px] flex items-center">
        <img src={HERO} alt="hero" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full py-20">
          <div className="max-w-2xl text-white animate-fade-up">
            <span className="inline-block text-xs font-semibold uppercase tracking-[0.2em] bg-white/15 backdrop-blur px-3 py-1.5 rounded-md mb-5">{t("hero.badge")}</span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter leading-[1.05] mb-4" data-testid="hero-title">{s.hero_title}</h1>
            <p className="text-lg text-white/85 mb-8 max-w-xl">{s.hero_subtitle}</p>
          </div>
          <div className="mt-4"><SearchWidget /></div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
            <div>
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("categories.title")}</h2>
              <p className="text-muted-foreground mt-2">{t("categories.subtitle")}</p>
            </div>
            <Button variant="outline" className="rounded-md gap-2 w-fit" onClick={() => navigate("/fleet")} data-testid="view-all-fleet">
              {t("nav.fleet")} <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.filter((c) => c.count > 0).map((cat, i) => (
              <button key={cat.key} onClick={() => navigate(`/fleet?category=${cat.key}`)} data-testid={`category-${cat.key}`}
                className="group relative overflow-hidden border border-border rounded-lg text-left animate-fade-up h-56" style={{ animationDelay: `${i * 60}ms` }}>
                <img src={cat.image || "https://images.pexels.com/photos/164634/pexels-photo-164634.jpeg?w=800"} alt={cat.key}
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 p-6 text-white">
                  <h3 className="font-heading font-bold text-2xl">{lang === "ro" ? cat.name_ro : cat.name_en}</h3>
                  <p className="text-sm text-white/80 mt-1">{cat.starting_price ? `${t("categories.from")} ${cat.starting_price} ${t("common.currency")}${t("categories.per_day")}` : `${cat.count} ${t("categories.cars")}`}</p>
                  <span className="inline-flex items-center gap-1 text-sm font-semibold mt-3 group-hover:gap-2 transition-all">{t("categories.view")} <ArrowRight className="h-4 w-4" /></span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Why us */}
      <section className="py-24 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("why.title")}</h2>
            <p className="text-muted-foreground mt-2">{t("why.subtitle")}</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyItems.map((item, i) => {
              const Icon = WHY_ICONS[i % WHY_ICONS.length];
              return (
                <div key={i} className="bg-white border border-border rounded-lg p-7 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`why-item-${i}`}>
                  <div className="bg-black text-white w-12 h-12 flex items-center justify-center rounded-md mb-4"><Icon className="h-6 w-6" /></div>
                  <h3 className="font-heading font-semibold text-lg mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {testimonials.length > 0 && (
        <section className="py-24">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto mb-14">
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">{t("testimonials.title")}</h2>
              <p className="text-muted-foreground mt-2">{t("testimonials.subtitle")}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((tm, i) => (
                <div key={i} className="border border-border rounded-lg p-7 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }} data-testid={`testimonial-${i}`}>
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: 5 }).map((_, sIdx) => <Star key={sIdx} className={`h-4 w-4 ${sIdx < tm.rating ? "fill-accent text-accent" : "text-border"}`} />)}
                  </div>
                  <p className="text-sm leading-relaxed mb-6">"{tm.text}"</p>
                  <div className="flex items-center gap-3">
                    {tm.avatar && <img src={tm.avatar} alt={tm.name} className="h-11 w-11 rounded-full object-cover" />}
                    <div><p className="font-semibold text-sm">{tm.name}</p><p className="text-xs text-muted-foreground">{tm.location}</p></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-black text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">{s.hero_title}</h2>
            <p className="text-white/70 mt-2">{s.hero_subtitle}</p>
          </div>
          <Button size="lg" className="rounded-md bg-accent hover:bg-accent/90 gap-2 shrink-0" onClick={() => navigate("/fleet")} data-testid="cta-fleet-btn">
            {t("nav.fleet")} <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
